"use client";

import {useEffect, useState} from "react";
import {useAuthContext} from "@/contexts/common/AuthContextProvider";
import {GailProductRoles} from "@/enums/user";
import {useStableCallback} from "@/hooks/useStableCallback";
import {useTourOnScreen} from "@/hooks/useTourOnScreen";
import {useTourShownThisVisit} from "@/hooks/useTourShownThisVisit";
import {useUserAcknowledgement} from "@/hooks/useUserAcknowledgement";
import {GAILGPT_LAUNCHER_TIP_ACKNOWLEDGEMENT} from "@/utils/acknowledgements/userAcknowledgements";
import {LAUNCHER_TIP_DELAY_MS} from "@/utils/chat/gailGptLauncher";

/**
 * First-visit tip, stored with the person's acknowledgements.
 * It always waits for a tour on screen to finish; `suppressAfterTour` also holds it back for the rest of that visit.
 */
export function useLauncherOnboarding(open: boolean, {suppressAfterTour = true} = {}) {
  const { user, productRolesState } = useAuthContext();
  const userId = user?.id;
  const orgId = user?.orgId;
  const tip = useUserAcknowledgement({ descriptor: GAILGPT_LAUNCHER_TIP_ACKNOWLEDGEMENT, orgId, userId });
  const tourShown = useTourShownThisVisit();
  const tourOnScreen = useTourOnScreen();

  const canShowTip = Boolean(userId && orgId) && productRolesState.loaded &&
    (productRolesState.roles?.includes(GailProductRoles.GailAgentUser) ?? false);
  const tipPending = canShowTip && tip.isResolved && !tip.isAcknowledged && !(suppressAfterTour && tourShown);
  const held = open || tourOnScreen;
  const [tipDue, setTipDue] = useState(false);
  useEffect(() => {
    if (!tipPending || held) return;
    const timer = window.setTimeout(() => setTipDue(true), LAUNCHER_TIP_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
      // Once released, the page settles again before the tip returns.
      setTipDue(false);
    };
  }, [tipPending, held]);

  const acknowledgeTip = useStableCallback((action: "dismiss" | "try" | "open") => {
    if (!tipPending) return;
    void tip.acknowledge(action === "dismiss" ? "dismissed" : "completed", { action });
  });

  return {
    tipVisible: tipPending && tipDue && !held,
    acknowledgeTip,
  };
}

